import type { AllAnalyticsEvents } from "@shared/models/analytics/all"
import type { LessonAnalyticsCore } from "@shared/models/analytics/lesson"
import type { Editor } from "@tiptap/core"
import { guardAnalytics } from "./instrument"
import type { OperationArgs, OperationContract, OperationName } from "./operations"
import {
  newInteractionId,
  type EditorSurface,
  type HostId,
  type InteractionId,
  type NonEmptyTuple,
} from "./types"

/**
 * An editor operation analytics event - the object passed to an AnalyticsRuntime's EditorAnalyticsPort.
 *
 * It describes context and newAnalyticsEvents that the AnalyticsRuntime turns into an action narrative.
 */
export interface EditorOperationEvent {
  readonly type: "operation"
  readonly operation: OperationName
  readonly surface: EditorSurface
  readonly args: unknown
  readonly interactionId: InteractionId
  projectOperationAnalytics(core: LessonAnalyticsCore): NonEmptyTuple<AllAnalyticsEvents>
  readonly hostId: HostId
  readonly occurredAt: string
}

/**
 * Reports an editing operation, so that it generates newAnalyticsEvents,
 * without going through the `label` command.
 *
 * Used for operations that don't generate a transaction/Tiptap command/chain:
 * the action mutates nothing (a clipboard write), ProseMirror mutates after the handler returns
 * (a paste), or the args only exist once the mutation is done (a minted card id).
 */
export function reportOperation<Contract extends OperationContract<unknown>>(
  editor: Editor,
  contract: Contract,
  surface: EditorSurface,
  args: NoInfer<OperationArgs<Contract>>,
): void {
  guardAnalytics(editor, (runtime) => {
    const event: EditorOperationEvent = {
      type: "operation",
      operation: contract.operationName as OperationName,
      surface: surface,
      args: args,
      interactionId: newInteractionId(),
      projectOperationAnalytics: (core) => contract.analyticsEvents({ args, core }),
      hostId: runtime.hostId,
      occurredAt: new Date().toISOString(),
    }
    validateEventIds(event)
    runtime.port.recordOperation(event)
  })
}

/**
 * Every domain id must be a non-empty string. Throwing hands the failure to the boundary, which
 * drops the event rather than shipping one with a blank id.
 */
function validateEventIds(event: { hostId: HostId }): void {
  const assertIds = (value: unknown, path: string): void => {
    if (value === null) return // nullable ids (e.g. newCardId, target cardId) are allowed
    if (Array.isArray(value)) {
      value.forEach((item, index) => assertIds(item, `${path}[${index}]`))
      return
    }
    if (typeof value !== "string" || value.length === 0) {
      throw new Error(`Editor analytics event has a blank id at ${path}`)
    }
  }
  const visit = (value: unknown, path: string): void => {
    if (value === null || value === undefined) return
    if (Array.isArray(value)) {
      value.forEach((item, index) => visit(item, `${path}[${index}]`))
      return
    }
    if (typeof value === "object") {
      for (const [key, child] of Object.entries(value)) {
        // An id value is a string or an array of strings — nothing inside it left to visit.
        if (key.endsWith("Id") || key.endsWith("Ids")) assertIds(child, `${path}.${key}`)
        else visit(child, `${path}.${key}`)
      }
    }
  }
  visit(event, "event")
}
